import { useState, useEffect, useMemo, useContext, useCallback } from 'react'
import { motion } from 'framer-motion'
import isEqual from 'lodash/isEqual'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { SchemaItem } from './schema-item'
import { EditTemplateContext } from '../edit-template-context'
import * as TemplateActions from '../template.actions'

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  margin: '10px 0',
  // styles we need to apply on draggables
  ...draggableStyle,
})

export const List = props => {
  const { children, scrollRef } = props
  const storeItems = useSelector(state => state.Template.templateFields, shallowEqual)
  const [stateItems, setStateItems] = useState(storeItems)
  const template = useSelector(state => state.Template.template)
  const templateFieldsById = useSelector(state => state.Template.templateFieldsById)
  const templateId = template.id
  const [fieldsMappedToIntegrations, setFieldsMappedToIntegrations] = useState([])

  const [CREATIVE_CLASSIFICATION_ENABLED = false, IMAGE_PREVIEW_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.CREATIVE_CLASSIFICATION, FeatureDict.IMAGE_PREVIEW]),
  )

  const dispatch = useDispatch()

  function onDragEnd(result) {
    const newItems = reorder(storeItems, result.source.index, result.destination.index)
    dispatch(TemplateActions.setFields(newItems))
  }

  const { notifications: issues } = useContext(EditTemplateContext)
  const focusIssue = issues.currentIssue && issues.currentIssue.reference.id
  const issuesById = [...issues.warnings, ...issues.errors].reduce((memo, item) => {
    memo[item.reference.fieldId] = item
    return memo
  }, {})

  useEffect(() => {
    api
      .get(`/api/v1/account/template/${templateId}/fieldsMappedToIntegrations`)
      .then(data => setFieldsMappedToIntegrations(data.data))
  }, [])

  useEffect(() => {
    if (focusIssue) {
      try {
        const elem = document.getElementById(focusIssue)
        scrollRef.current.scrollTo({ top: elem.offsetTop - 80, behavior: 'smooth' })
      } catch {
        // not a thing
      }
    }
  }, [focusIssue])

  useMemo(() => {
    if (!isEqual(storeItems, stateItems)) {
      setStateItems(storeItems)
    }
  }, [storeItems])

  const isMappedToIntegration = useCallback(
    id => fieldsMappedToIntegrations.includes(id),
    [fieldsMappedToIntegrations],
  )

  const selectedFieldIds = stateItems.map(id => templateFieldsById[id]?.field_id)
  const getSelectedFieldIds = useCallback(() => {
    return selectedFieldIds
  }, [selectedFieldIds])

  const hasSourceUrl = Object.values(templateFieldsById).some(
    field => field.classification_parameter_type === 'SOURCE_URL',
  )

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {provided => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {stateItems.map((item, index) => (
              <Draggable key={item} draggableId={`${item}`} index={index}>
                {(provided, snapshot) => (
                  <motion.div
                    animate={{
                      skewY:
                        focusIssue && focusIssue === `SCHEMA_ITEM_${item}`
                          ? [0, -1, 0, 1, 0, -1, 0, 1, 0]
                          : 0,
                      transition: { delay: 0.2, duration: 1 },
                    }}
                    initial={false}
                  >
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                      <SchemaItem
                        id={item}
                        hasSourceUrl={hasSourceUrl}
                        isCreativeClassificationEnabled={template.isCreativeClassificationEnabled}
                        index={index}
                        reorderMode={issues.reorderMode}
                        issue={issuesById[item]}
                        isMappedToIntegration={isMappedToIntegration(item)}
                        isImagePreviewEnabled={IMAGE_PREVIEW_ENABLED}
                        isCreateClassificationFeatureEnabled={CREATIVE_CLASSIFICATION_ENABLED}
                        getSelectedFieldIds={getSelectedFieldIds}
                      />
                    </div>
                  </motion.div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {children}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
